import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sparkles, X } from 'lucide-react';

/**
 * ProactiveLynk — gently surfaces a Cara nudge when a user seems stuck.
 *
 * Triggers (per-route, fires at most ONCE per route visit):
 *  - LINGER: user spends > N seconds on a "high-intent" route without navigating
 *  - RAGE: user rage-clicks (3+ taps on same target inside 1.2s)
 *  - SCROLL_THRASH: user scrolls up/down repeatedly without engaging
 *
 * Suppressed if user dismissed the nudge less than 24h ago globally.
 */

const HIGH_INTENT_ROUTES = {
  '/patient/find-clinicians': { linger: 75, message: "Finding the right clinician can feel like a lot. Want me to help narrow it down?" },
  '/patient/navigator': { linger: 90, message: "Stuck on a question? Tell me what you're thinking and I'll suggest the right answer." },
  '/patient/offers': { linger: 60, message: "Negotiating an offer? I can explain what each option means in plain English." },
  '/patient/assessment': { linger: 60, message: "Some of these questions are tricky. Want me to translate?" },
  '/clinician/assessment': { linger: 60, message: "Want me to help draft any of these fields from a quick description?" },
  '/clinician/offers': { linger: 60, message: "Need a second opinion on whether to accept or counter? I can help you think it through." },
  '/patient/dashboard': { linger: 120, message: "Looking for something specific? Just ask — I'll point you to the right spot." },
  '/clinician/dashboard': { linger: 120, message: "Want a quick tour of what's new, or help finishing today's notes?" },
};

const DISMISS_KEY = 'cn-proactive-lynk-dismissed-at';
const RESPECT_HOURS = 24;

export const ProactiveLynk = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [nudge, setNudge] = useState(null); // { message }
  const firedThisRouteRef = useRef(false);
  const timerRef = useRef(null);
  const rageRef = useRef({ target: null, count: 0, last: 0 });

  // Reset per-route
  useEffect(() => {
    firedThisRouteRef.current = false;
    setNudge(null);
    clearTimeout(timerRef.current);
    if (!user) return;

    // Respect global cooldown
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (Date.now() - dismissedAt < RESPECT_HOURS * 60 * 60 * 1000) return;

    const config = HIGH_INTENT_ROUTES[location.pathname];
    if (!config) return;

    // LINGER timer
    timerRef.current = setTimeout(() => {
      if (firedThisRouteRef.current) return;
      firedThisRouteRef.current = true;
      setNudge({ message: config.message });
    }, config.linger * 1000);

    return () => clearTimeout(timerRef.current);
  }, [location.pathname, user]);

  // RAGE click detector
  useEffect(() => {
    if (!user) return;
    const handler = (e) => {
      if (firedThisRouteRef.current) return;
      const now = Date.now();
      const r = rageRef.current;
      if (r.target === e.target && now - r.last < 1200) {
        r.count += 1;
      } else {
        r.target = e.target;
        r.count = 1;
      }
      r.last = now;
      if (r.count >= 3) {
        firedThisRouteRef.current = true;
        setNudge({ message: "Looks like this isn't responding the way you'd expect. Want me to take a look?" });
        rageRef.current = { target: null, count: 0, last: 0 };
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [user]);

  const dismiss = (suppress) => {
    setNudge(null);
    if (suppress) localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const openLynk = () => {
    setNudge(null);
    // Open the Cara panel by triggering the FAB
    document.querySelector('[data-testid="lynk-fab"]')?.click();
  };

  if (!nudge || !user) return null;

  return (
    <div
      className="fixed z-[58] bottom-20 right-4 sm:right-7 sm:bottom-24 max-w-[320px] animate-fade-in-up"
      data-testid="proactive-lynk-nudge"
      role="dialog"
      aria-label="Cara assistant suggestion"
    >
      <div className="relative rounded-2xl bg-white dark:bg-[#0F172A] border border-cn-border/60 shadow-2xl shadow-cn-primary/15 overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: 'linear-gradient(180deg, #0F172A, #334155)' }} />
        <button
          onClick={() => dismiss(false)}
          aria-label="Dismiss suggestion"
          data-testid="proactive-lynk-dismiss"
          className="absolute top-2 right-2 p-1 rounded-full text-cn-text-muted hover:bg-cn-border/40"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="p-4 pl-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <span className="cn-eyebrow">Cara · here to help</span>
          </div>
          <p className="text-sm text-cn-text dark:text-white leading-relaxed">{nudge.message}</p>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={openLynk}
              data-testid="proactive-lynk-open"
              className="cn-btn cn-btn-primary !py-2 !px-4 !text-xs"
            >
              Yes, help me
            </button>
            <button
              onClick={() => dismiss(true)}
              data-testid="proactive-lynk-not-now"
              className="text-xs font-semibold text-cn-text-muted hover:text-cn-text px-2"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProactiveLynk;
